import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {uniqueId} from 'lodash';
import {Button, EMPHASIS, Tooltip} from '@forecast-it/design-system';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {BaselineVersionCompareQuery} from './__generated__/BaselineVersionCompareQuery.graphql';
import {ActiveMark, useActiveTip, VisuallyHidden} from './ActiveMark';
import {buildVersionSummary} from './buildVersionSummary';
import {compareFigures, CompareFigures, delta, deltaTone, formatMoneyCents, Metric} from './baselineVersionsMath';
import {RenameInput} from './RenameInput';
import {VersionMenu} from './VersionMenu';
import {VersionName} from './VersionName';
import {C, F} from './versionsStyles';

const COMPARE_QUERY = graphql`
	query BaselineVersionCompareQuery($projectId: ID) {
		viewer {
			component(name: "baseline_version_compare")
			project(internalId: $projectId) {
				baselineVersions {
					id
					name
					isStandard
					isActive
					summary {
						revenue
						cost
						profit
						margin
						minutes
					}
					origin {
						source
						scaleDirection
						scalePercent
						templateName
						templateDescription
					}
					phaseTotals {
						name
						minutes
						roleCount
					}
				}
			}
		}
	}
`;

type CompareVersion = NonNullable<
	NonNullable<NonNullable<BaselineVersionCompareQuery['response']['viewer']>['project']>['baselineVersions']
>[number];

export interface BaselineVersionCompareProps {
	projectId: string;
	/** Changes whenever the page's version list changes (create, rename, delete), so the table refetches. */
	refreshKey: string;
	selectedId: string | null;
	currencySymbol: string;
	/** The create panel, shown beside the table while open. */
	adding: React.ReactNode | null;
	/** The active version is locked by a quote: its mark reads "Active · Quoted" with a lock. */
	locked: boolean;
	/** Picks the active mark's tooltip wording, with or without the quote. */
	quotesEnabled: boolean;
	onSelect: (id: string) => void;
	/** Called with the trimmed new name; never with an empty or unchanged one. */
	onRename: (id: string, name: string) => void;
	onDelete: (id: string, name: string) => void;
	onClose: () => void;
}

const Box = styled.div`
	background: ${C.tint};
	border-radius: 8px;
	padding: 12px 16px 16px;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 12px;
`;

const Title = styled.span`
	${F.sub2}
	color: ${C.text};
`;

const Muted = styled.span`
	${F.body2}
	color: ${C.aux};
	margin-left: 10px;
`;

const Scroll = styled.div`
	overflow-x: auto;
`;

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`;

const Grid = styled.div<{$columns: number}>`
	flex: 1 1 auto;
	min-width: ${p => 120 + p.$columns * 180}px;
	display: grid;
	grid-template-columns: 120px repeat(${p => p.$columns}, minmax(180px, 1fr));
	align-content: start;
`;

const Corner = styled.div`
	padding: 12px 0;
	align-self: end;
	${F.detail}
	color: ${C.aux};
`;

const Column = styled.div<{$selected: boolean}>`
	background: ${p => (p.$selected ? C.surface : 'transparent')};
	min-width: 0;
`;

const ColumnHead = styled(Column)`
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 52px;
	padding: 6px 8px 6px 12px;
	box-sizing: border-box;
	border-radius: 4px 4px 0 0;
`;

const NameButton = styled.button<{$selected: boolean}>`
	flex: 0 1 auto;
	min-width: 0;
	max-width: 100%;
	display: flex;
	border: none;
	background: transparent;
	padding: 0;
	text-align: left;
	cursor: ${p => (p.$selected ? 'default' : 'pointer')};
	${F.sub2}
	color: ${p => (p.$selected ? C.text : C.brand)};
`;

const Label = styled.div`
	padding: 10px 0;
	border-top: 1px solid ${C.border};
	${F.body2}
	color: ${C.aux};
`;

const Cell = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	flex-wrap: wrap;
	padding: 10px 16px;
	border-top: 1px solid ${C.border};
	${F.body2}
	color: ${C.text};
	font-variant-numeric: tabular-nums;
`;

const TONE_COLOUR = {good: C.success, bad: C.error, neutral: C.aux};

const Delta = styled.span<{$tone: 'good' | 'bad' | 'neutral'}>`
	${F.sub2}
	color: ${p => TONE_COLOUR[p.$tone]};
`;

const SummaryCell = styled.div`
	padding: 10px 12px;
	border-top: 1px solid ${C.border};
	${F.body2}
	color: ${C.text};
`;

const Panel = styled.div`
	flex: 0 0 420px;
`;

const Spacer = styled.span`
	flex: 1 1 auto;
`;

const MarkHost = styled.span`
	display: flex;
	flex: none;
`;

const Status = styled.div`
	position: relative;
	flex: 1 1 auto;
	min-height: 120px;
	${F.body2}
	color: ${C.text};
`;

interface ActiveInfo {
	locked: boolean;
	tip: string;
	/** Id of the hidden tip text, so the active name button announces it. */
	tipId: string;
}

interface ColumnHeaderProps {
	version: CompareVersion;
	active: ActiveInfo;
	selected: boolean;
	/** False only for the active version when no other non-Standard version could become active. */
	canDelete: boolean;
	renaming: boolean;
	draft: string;
	onDraftChange: (value: string) => void;
	onStartRename: () => void;
	onCommitRename: () => void;
	onCancelRename: () => void;
	onSelect: () => void;
	onDelete: () => void;
}

/**
 * How long a mouse click on a renamable name waits before selecting, so the second click of a double-click can start a
 * rename instead. Selecting reloads the page, which would otherwise swallow that second click.
 */
const SELECT_DELAY_MS = 300;

/**
 * One column's header: the name (a button that selects the version's tab), or the rename field while renaming, the
 * Active mark on the active version, and the version menu. The name button is the only focus stop for the name, so it
 * carries the full-name tooltip itself while the name is cut off. The Active mark is plain text beside it with a hover
 * tooltip; keyboard and screen-reader users get the same tip as the name button's description. A mouse click on a
 * renamable name selects only once no double-click follows; Standard and keyboard presses select at once.
 */
const ColumnHeader = ({
	version,
	active,
	selected,
	canDelete,
	renaming,
	draft,
	onDraftChange,
	onStartRename,
	onCommitRename,
	onCancelRename,
	onSelect,
	onDelete,
}: ColumnHeaderProps) => {
	const [cut, setCut] = useState(false);
	const name = version.name ?? '';
	const pendingSelect = useRef<number>();
	const latestSelect = useRef(onSelect);
	latestSelect.current = onSelect;
	useEffect(() => () => window.clearTimeout(pendingSelect.current), []);
	const onNameClick = (event: React.MouseEvent) => {
		window.clearTimeout(pendingSelect.current);
		// A keyboard press reports no clicks (detail 0) and cannot be the start of a double-click.
		if (version.isStandard || event.detail === 0) {
			onSelect();
			return;
		}
		pendingSelect.current = window.setTimeout(() => latestSelect.current(), SELECT_DELAY_MS);
	};
	const onNameDoubleClick = () => {
		window.clearTimeout(pendingSelect.current);
		onStartRename();
	};
	const nameButton = (
		<NameButton
			type="button"
			$selected={selected}
			onClick={onNameClick}
			onDoubleClick={version.isStandard ? undefined : onNameDoubleClick}
			aria-describedby={version.isActive ? active.tipId : undefined}
			data-cy={`baseline-compare-name-${name}`}
		>
			<VersionName name={name} onCutChange={setCut} />
		</NameButton>
	);
	let title: React.ReactNode = nameButton;
	if (renaming) {
		title = <RenameInput value={draft} onChange={onDraftChange} onCommit={onCommitRename} onCancel={onCancelRename} />;
	} else if (cut) {
		title = (
			<Tooltip content={name} width="260px">
				{nameButton}
			</Tooltip>
		);
	}
	return (
		<ColumnHead $selected={selected} data-testid={`baseline-compare-column-${version.id}`}>
			{title}
			{version.isActive ? (
				<Tooltip content={active.tip} width="260px">
					<MarkHost>
						<ActiveMark locked={active.locked} />
					</MarkHost>
				</Tooltip>
			) : null}
			<Spacer />
			{version.isStandard ? null : (
				<VersionMenu name={name} canDelete={canDelete} onRename={onStartRename} onDelete={onDelete} />
			)}
		</ColumnHead>
	);
};

interface MetricRow {
	metric: Metric;
	label: string;
	show: (n: number) => string;
	/** Formats a non-zero difference from Standard, always signed. */
	showDelta: (d: number) => string;
}

const EMPTY_SUMMARY = {revenue: 0, cost: null, profit: null, margin: null, minutes: 0};

const sign = (d: number) => (d > 0 ? '+' : '-');

/**
 * The compare table under the version tabs: each version's Revenue, Cost, Profit, Margin and Hours with its
 * difference from Standard, and a plain-words Summary. Its numbers are the same `summary` the banner shows, so the two
 * agree. Cost, Profit and Margin are hidden when the viewer has no cost access.
 */
export const BaselineVersionCompare = ({
	projectId,
	refreshKey,
	selectedId,
	currencySymbol,
	adding,
	locked,
	quotesEnabled,
	onSelect,
	onRename,
	onDelete,
	onClose,
}: BaselineVersionCompareProps) => {
	const {formatMessage} = useIntl();
	const {fetch} = useForecastFetchQuery<BaselineVersionCompareQuery['response'], BaselineVersionCompareQuery['variables']>(
		COMPARE_QUERY
	);
	const [versions, setVersions] = useState<CompareVersion[] | null>(null);
	const [failed, setFailed] = useState(false);
	const latestRequest = useRef(0);
	// Fresh on every open and after every change to the version list, all versions in one request. Only the latest
	// request's answer is used, so a slow earlier one can't overwrite it, and nothing lands after the table closes.
	// A failure drops the numbers rather than leaving outdated ones on screen. `fetch` is left out of the dependencies
	// because the hook recreates it on every render.
	useEffect(() => {
		latestRequest.current += 1;
		const request = latestRequest.current;
		const isLatest = () => request === latestRequest.current;
		setFailed(false);
		fetch(
			{projectId},
			response => {
				if (isLatest()) {
					setVersions(
						(response.viewer?.project?.baselineVersions ?? []).filter((v): v is CompareVersion => v != null)
					);
				}
			},
			() => {
				if (isLatest()) {
					setVersions(null);
					setFailed(true);
				}
			}
		);
		return () => {
			latestRequest.current += 1;
		};
	}, [projectId, refreshKey]);

	const [renaming, setRenaming] = useState<string | null>(null);
	const [draft, setDraft] = useState('');
	const activeTip = useActiveTip(quotesEnabled);
	const activeTipId = useMemo(() => uniqueId('baseline-compare-active-tip-'), []);
	const active: ActiveInfo = {locked, tip: activeTip, tipId: activeTipId};

	const all = versions ?? [];
	const standard = all.find(v => v.isStandard) ?? all[0];

	const shell = (body: React.ReactNode) => (
		<Box data-cy="baseline-version-compare">
			<Header>
				<span>
					<Title>{formatMessage({id: 'baseline.versions.compare_title'})}</Title>
					{standard ? (
						<Muted>{formatMessage({id: 'baseline.versions.compare_against'}, {name: standard.name})}</Muted>
					) : null}
				</span>
				<Button
					emphasis={EMPHASIS.LOW}
					icon="close"
					aria-label={formatMessage({id: 'baseline.versions.compare_close'})}
					onPress={onClose}
				/>
			</Header>
			<Scroll>
				<Row>
					{body}
					{adding ? <Panel>{adding}</Panel> : null}
				</Row>
			</Scroll>
			<VisuallyHidden id={activeTipId}>{active.tip}</VisuallyHidden>
		</Box>
	);

	if (failed) {
		return shell(
			<Status role="alert" data-testid="baseline-compare-error">
				{formatMessage({id: 'error_boundary.message1'})}
			</Status>
		);
	}
	if (!standard) {
		return shell(
			versions == null ? (
				<Status data-testid="baseline-compare-loading">
					<InlineLoader />
				</Status>
			) : null
		);
	}
	const rows = [standard, ...all.filter(v => v !== standard)];
	const seesCost = standard.summary?.cost != null;
	const figures = new Map<string, CompareFigures>(rows.map(v => [v.id, compareFigures(v.summary ?? EMPTY_SUMMARY)]));
	const std = figures.get(standard.id) as CompareFigures;

	const money = (n: number) => formatMoneyCents(n, currencySymbol);
	const moneyDelta = (d: number) => sign(d) + money(Math.abs(d));
	const moneyRow = (metric: Metric, label: string): MetricRow => ({metric, label, show: money, showDelta: moneyDelta});
	const metrics: MetricRow[] = [
		moneyRow('revenue', 'metric_revenue'),
		...(seesCost
			? [
					moneyRow('cost', 'metric_cost'),
					moneyRow('profit', 'metric_profit'),
					{
						metric: 'margin' as Metric,
						label: 'metric_margin',
						show: (n: number) => `${n}%`,
						showDelta: (d: number) => `${sign(d)}${Math.abs(d)}%`,
					},
			  ]
			: []),
		{metric: 'hours', label: 'metric_hours', show: n => `${n}h`, showDelta: d => `${sign(d)}${Math.abs(d)}h`},
	];

	const startRename = (v: CompareVersion) => {
		setDraft(v.name ?? '');
		setRenaming(v.id);
	};
	const commit = (v: CompareVersion) => {
		if (renaming !== v.id) {
			return;
		}
		const name = draft.trim();
		// An empty name quietly keeps the old one, and an unchanged name is not sent.
		if (name && name !== v.name) {
			onRename(v.id, name);
		}
		// Unmounting the field here is what stops a blur after Enter from saving a second time.
		setRenaming(null);
	};

	return shell(
		<Grid $columns={rows.length}>
			<Corner>{formatMessage({id: 'baseline.versions.compare_vs'}, {name: standard.name})}</Corner>
			{rows.map(v => (
				<ColumnHeader
					key={v.id}
					version={v}
					active={active}
					selected={v.id === selectedId}
					canDelete={!v.isActive || all.some(o => !o.isStandard && o.id !== v.id)}
					renaming={renaming === v.id}
					draft={draft}
					onDraftChange={setDraft}
					onStartRename={() => startRename(v)}
					onCommitRename={() => commit(v)}
					onCancelRename={() => setRenaming(null)}
					onSelect={() => onSelect(v.id)}
					onDelete={() => onDelete(v.id, v.name ?? '')}
				/>
			))}
			{metrics.map(({metric, label, show, showDelta}) => (
				<React.Fragment key={metric}>
					<Label>{formatMessage({id: `baseline.versions.${label}`})}</Label>
					{rows.map(v => {
						const f = figures.get(v.id) as CompareFigures;
						const value = f[metric];
						const d = v.id === standard.id ? null : delta(metric, f, std);
						return (
							<Column key={v.id} $selected={v.id === selectedId}>
								<Cell
									data-testid={`baseline-compare-${v.id}-${metric}`}
									data-cy={`baseline-compare-${v.name}-${metric}`}
								>
									<span>{value == null ? '' : show(value)}</span>
									{d ? (
										<Delta
											data-testid="delta"
											data-tone={deltaTone(metric, d)}
											$tone={deltaTone(metric, d)}
										>
											{showDelta(d)}
										</Delta>
									) : null}
								</Cell>
							</Column>
						);
					})}
				</React.Fragment>
			))}
			<Label>{formatMessage({id: 'baseline.versions.compare_summary'})}</Label>
			{rows.map(v => (
				<Column key={v.id} $selected={v.id === selectedId}>
					<SummaryCell
						data-testid={`baseline-compare-${v.id}-summary`}
						data-cy={`baseline-compare-${v.name}-summary`}
					>
						{buildVersionSummary(standard, v, {formatMessage, currencySymbol, seesCost})}
					</SummaryCell>
				</Column>
			))}
		</Grid>
	);
};
